import React, { useState, useEffect, useRef } from 'react';
import ContactForm from '../components/Contact/ContactForm';
import GoogleMap from '../components/Contact/GoogleMap';
import { locations } from '../constants/data';
import { MapPin, Phone, Mail, MessageSquare, Send, ChevronRight, Building2, ArrowDownRight } from 'lucide-react';

import cave from "../assets/fphc_cave.jpeg"

const Contact = () => {
  const [selectedLocation, setSelectedLocation] = useState(locations[0]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    });

    document.querySelectorAll('.animate-on-scroll').forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleLocationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    locationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFormClick = (e: React.MouseEvent) => {
    e.preventDefault();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const contactMethods = [
    {
      icon: Phone,
      title: 'Telefonisch',
      description: 'Rufen Sie uns an für eine direkte Beratung',
      action: {
        text: '+43 664 470 48 58',
        href: 'tel:+436644704858'
      }
    },
    {
      icon: Mail,
      title: 'Per E-Mail',
      description: 'Schreiben Sie uns eine E-Mail',
      action: {
        text: 'office@fphc.at',
        href: 'mailto:office@fphc.at'
      }
    },
    {
      icon: MessageSquare,
      title: 'Kontaktformular',
      description: 'Nutzen Sie unser Online-Formular',
      action: {
        text: 'Zum Formular',
        href: '#contact-form'
      }
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[70vh]">
        <div className="absolute inset-0">
          <img 
            src={cave}
            alt="Environmental background"
            className="w-full h-full object-cover brightness-[0.4]"
          />
        </div>

        <div className="relative container mx-auto px-4 py-24 pt-32 pb-32 sm:pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl mb-8 animate-fade-in">
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 bg-[#75B2DD] rounded-[2px] animate-pulse-first" />
                <div className="w-2.5 h-2.5 bg-[#B5E2BF] rounded-[2px] animate-pulse-second" />
                <div className="w-2.5 h-2.5 bg-[#009E60] rounded-[2px] animate-pulse-third" />
              </div>
              <span className="text-success font-medium">Kontakt</span>
            </div>

            <div className="space-y-8 animate-slide-up">
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight">
                  Lassen Sie uns über Ihre <span className="text-[#B5E2BF]">Umweltziele</span> sprechen
                </h1>
                <div className="w-20 h-1.5 bg-gradient-to-r from-success to-success/30 rounded-full" />
                <p className="text-gray-300 text-base sm:text-xl leading-relaxed">
                  Unser Team von Experten steht Ihnen zur Verfügung, um nachhaltige Lösungen für Ihre Herausforderungen zu entwickeln.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-scale-in" style={{ animationDelay: '0.3s' }}>
                <a
                  href="#contact-form"
                  onClick={handleFormClick}
                  className="group bg-success hover:bg-dark text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-300 inline-flex items-center justify-center space-x-2 shadow-lg shadow-success/20 text-sm sm:text-base"
                >
                  <span>Kontaktformular</span>
                  <ArrowDownRight className="w-4 sm:w-5 h-4 sm:h-5 transform group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
                </a>
                <a
                  href="#location"
                  onClick={handleLocationClick}
                  className="group bg-white/10 hover:bg-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-300 inline-flex items-center justify-center space-x-2 backdrop-blur-sm text-sm sm:text-base"
                >
                  <span>Standort finden</span>
                  <MapPin className="w-4 sm:w-5 h-4 sm:h-5 transform group-hover:translate-y-[-2px] transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Diagonal Cut 
        <div className="absolute -bottom-1 left-0 w-full h-32 overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-24 sm:h-32 bg-white"
               style={{
                 clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 90%)'
               }}
          />
        </div>
        */}
      </section>

      {/* Contact Methods Section */}
      <section id="contact-methods" className="relative bg-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 animate-on-scroll">
              <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
                <MessageSquare className="w-5 h-5 text-success" />
                <span className="text-success font-medium">Kontaktmöglichkeiten</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">Wie können wir Ihnen helfen?</h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Wählen Sie Ihren bevorzugten Weg der Kontaktaufnahme. Wir sind für Sie da.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => {
                const Icon = method.icon;
                return (
                  <div
                    key={method.title}
                    className="animate-on-scroll h-full"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <a
                      href={method.action.href}
                      className="group flex flex-col h-full bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="flex-1 space-y-6">
                        <div className="w-14 h-14 bg-success/5 rounded-xl flex items-center justify-center group-hover:bg-success/10 transition-colors">
                          <Icon className="w-7 h-7 text-success" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-2 group-hover:text-success transition-colors">
                            {method.title}
                          </h3>
                          <p className="text-gray-600 mb-4">{method.description}</p>
                        </div>
                      </div>
                      <div className="inline-flex items-center space-x-2 text-success group-hover:text-dark transition-colors">
                        <span>{method.action.text}</span>
                        <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section id="contact-form" ref={formRef} className="bg-gray-50 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              <div className="space-y-8 animate-on-scroll">
                <div>
                  <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
                    <Send className="w-5 h-5 text-success" />
                    <span className="text-success font-medium">Kontaktformular</span>
                  </div>
                  <h2 className="text-4xl font-bold mb-6">Schreiben Sie uns</h2>
                  <p className="text-gray-600 text-lg">
                    Nutzen Sie unser Kontaktformular für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen.
                  </p>
                </div>
                <ContactForm />
              </div>

              <div className="space-y-8 animate-on-scroll" ref={locationRef}>
                <div>
                  <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
                    <MapPin className="w-5 h-5 text-success" />
                    <span className="text-success font-medium">Anfahrt</span>
                  </div>
                  <h2 className="text-4xl font-bold mb-6">Besuchen Sie uns</h2>
                  <p className="text-gray-600 text-lg mb-8">
                    Wählen Sie einen unserer Standorte, um die genaue Position auf der Karte zu sehen.
                  </p>
                </div>

                <div className="grid gap-6 mb-8">
                  {locations.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => setSelectedLocation(location)}
                      className={`w-full text-left group bg-white rounded-xl p-6 transition-all duration-300 ${
                        selectedLocation.id === location.id 
                          ? 'ring-2 ring-success shadow-lg' 
                          : 'hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                          selectedLocation.id === location.id 
                            ? 'bg-success text-white' 
                            : 'bg-success/5 text-success group-hover:bg-success/10'
                        }`}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{location.city}</h3>
                          <div className="space-y-2 text-gray-600">
                            <p>{location.address}</p>
                            <div className="flex items-center gap-4 flex-wrap">
                              <a 
                                href={`tel:${location.phone}`}
                                className="text-success hover:text-dark transition-colors whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {location.phone}
                              </a>
                              <span className="text-gray-300">|</span>
                              <a 
                                href={`mailto:${location.email}`}
                                className="text-success hover:text-dark transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {location.email}
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 transition-colors ${
                          selectedLocation.id === location.id 
                            ? 'border-success bg-success' 
                            : 'border-gray-300 group-hover:border-success'
                        }`}>
                          {selectedLocation.id === location.id && (
                            <div className="w-full h-full bg-white rounded-full transform scale-50" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <GoogleMap center={selectedLocation.coordinates} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;